import React, { useEffect } from "react";
import DataTable, { TableColumn } from "react-data-table-component";
import { Card, CardBody, CardHeader, Col, Row } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import ViewButtonComponent from "../../../components/ViewButtonComponent";
import ButtonComponentV2 from "../../../components/ButtonComponentV2";
import BreadCrumbs from "../../../components/BreadCrumbs";
import DeleteButtonComponent from "../../../components/DeleteButtonComponent";
import TableLoadingComponent from "../../../components/TableLoadingComponent";
import { 
  OrganizationResponse, 
  useLazyGetAllOrganizationsQuery, 
  useDeleteOrganizationMutation 
} from "../../../redux/services/orgService";

const OrganizationList: React.FC = () => {
  const navigate = useNavigate();

  const [
    triggerGetAllOrganizations,
    { 
      data: organizationList, 
      isLoading: isOrganizationsLoading,
      error: organizationsError 
    },
  ] = useLazyGetAllOrganizationsQuery();

  const [deleteOrganization, { isLoading: isDeleting }] = useDeleteOrganizationMutation();

  useEffect(() => {
    // Fetch organizations when component mounts
    triggerGetAllOrganizations();
  }, [triggerGetAllOrganizations]);

  const handleViewOrganization = (organization: OrganizationResponse) => {
  navigate(`/organizations/organization-list/${organization.organizationId}`, {
    state: { 
      organization,
      // Pass organization name for breadcrumb context
      breadcrumbName: organization.displayName 
    },
  });
};

  const handleDeleteOrganization = async (organization: OrganizationResponse) => {
  const result = await Swal.fire({
    title: 'Are you sure?',
    text: `You are about to delete "${organization.displayName}". This action cannot be undone!`,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#d33',
    cancelButtonColor: '#6c757d',
    confirmButtonText: 'Yes, delete it!',
    cancelButtonText: 'Cancel',
    reverseButtons: true,
    customClass: {
      confirmButton: 'btn btn-danger mx-2',
      cancelButton: 'btn btn-secondary mx-2',
      actions: 'd-flex justify-content-center gap-3'
    },
    buttonsStyling: false
  });

  if (result.isConfirmed) {
    try {
      await deleteOrganization(organization.organizationId).unwrap();
      
      Swal.fire({
        title: 'Deleted!',
        text: `Organization "${organization.displayName}" has been deleted successfully.`,
        icon: 'success',
        confirmButtonText: 'OK',
        customClass: {
          confirmButton: 'btn btn-success'
        },
        buttonsStyling: false
      });

      triggerGetAllOrganizations();

    } catch (error) {
      console.error("Failed to delete organization:", error);
      
      Swal.fire({
        title: 'Error!',
        text: 'Failed to delete organization. Please try again.',
        icon: 'error',
        confirmButtonText: 'OK',
        customClass: {
          confirmButton: 'btn btn-danger'
        },
        buttonsStyling: false
      });
    }
  }
};

  const handleCreateOrganization = () => {
    // Navigate to create organization page
    navigate("/organizations/organization-list/add-organization");
  };

  const columns: TableColumn<OrganizationResponse>[] = [
    {
      name: <h6 className="fw-bold">Organization Name</h6>,
      selector: (row) => row.displayName,
      cell: (row) => (
        <div style={{ fontSize: "14px", fontWeight: "bold" }}>{row.displayName}</div>
      ),
      sortable: true,
      width: "200px",
    },
    {
      name: <h6 className="fw-bold">Code</h6>,
      selector: (row) => row.code,
      cell: (row) => <div style={{ fontSize: "14px" }}>{row.code}</div>,
      sortable: true,
    },
    {
      name: <h6 className="fw-bold">Contact Person</h6>,
      cell: (row) => (
        <div style={{ fontSize: "14px" }}>
          {row.deliveryPoint?.deliveryContactName || ""}
        </div>
      ),
      sortable: true,
      width: "200px",
    },
    {
      name: <h6 className="fw-bold">Credit Limit</h6>,
      selector: (row) => row.walletData?.creditLimit,
      cell: (row) => (
        <div style={{ fontSize: "14px" }}>
          LKR {row.walletData?.creditLimit?.toLocaleString() || "0"}
        </div>
      ),
      sortable: true,
    },
    {
      name: <h6 className="fw-bold">Available Credit</h6>,
      selector: (row) => row.creditSettings?.availableCredit,
      cell: (row) => (
        <div style={{ fontSize: "14px" }}>
          LKR {row.creditSettings?.availableCredit?.toLocaleString() || "0"}
        </div>
      ),
      sortable: true,
    },
    {
      name: <h6 className="fw-bold">Credit Terms</h6>,
      selector: (row) => row.creditSettings?.creditTermsDays,
      cell: (row) => (
        <div style={{ fontSize: "14px" }}>
          {row.creditSettings?.creditTermsDays || 0} days
        </div>
      ),
      sortable: true,
    },
    {
      name: <h6 className="fw-bold">Credit Orders</h6>,
      cell: (row) => (
        <span
          className={
            row.creditSettings?.allowCreditOrders
              ? "text-success"
              : "text-danger"
          }
        >
          {row.creditSettings?.allowCreditOrders ? "Allowed" : "Not Allowed"}
        </span>
      ),
      sortable: true,
    },
    {
      name: <h6 className="fw-bold">Status</h6>,
      selector: (row) => row.status,
      cell: (row) => (
        <span
          className={
            row.status === "Active" ? "text-success" : "text-danger"
          }
        >
          {row.status}
        </span>
      ),
      sortable: true,
    },
    {
      name: <h6 className="fw-bold">Action</h6>,
      cell: (row) => (
        <div className="d-flex gap-2">
          <ViewButtonComponent onClick={() => handleViewOrganization(row)} />
          <DeleteButtonComponent
            onClick={() => handleDeleteOrganization(row)}
            disabled={isDeleting}
          />
        </div>
      ),
      sortable: false,
      width: "150px",
    },
  ];

  return (
    <>
      <BreadCrumbs hasSubMenus={true} />
      <div className="mt-2">
        <Card>
          <CardHeader className="card-header">
            <Row className="align-items-center">
              <Col xs={12} md={6} lg={6}>
                <h5 className="m-0">Organization List</h5>
              </Col>
              <Col xs={12} md={6} lg={6}>
                <div className="d-flex justify-content-md-end justify-content-start mt-2 mt-md-0">
                  <ButtonComponentV2
                    titel="Add Organization"
                    onClick={handleCreateOrganization}
                    color="#fff"
                    backgroundColor="#590B21"
                  />
                </div>
              </Col>
            </Row>
            <hr className="mt-2" />
          </CardHeader>
          <CardBody>
            {isOrganizationsLoading ? (
              <TableLoadingComponent numColumns={6} />
            ) : organizationsError ? (
              <div className="text-center text-danger">
                Error loading organizations. Please try again.
              </div>
            ) : (
              <DataTable
                columns={columns}
                data={organizationList || []}
                responsive
                pagination
                highlightOnHover
                pointerOnHover
                paginationPerPage={10}
                noDataComponent={<div>No Organizations found</div>}
                progressPending={isOrganizationsLoading}
              />
            )}
          </CardBody>
        </Card>
      </div>
    </>
  );
};

export default OrganizationList;